import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDeviceChecker } from '@mentimeter/ragnar-device';
import { TourOverlay } from '../TourOverlay';
import { TOUR_ANIMATION_DURATION } from '../../constants';
import { TourPopover, type TourPopoverProps } from './TourPopover';

interface TourPopoverWithOverlayProps extends TourPopoverProps {
  overlayTarget?: string | HTMLElement | null;
  overlayColor?: string | undefined;
  spotlightPadding?: number | undefined;
  spotlightRadius?: number | undefined;
  onOverlayClick?: () => void;
  onLoad?: () => void;
}

export const TourPopoverWithOverlay: React.FC<TourPopoverWithOverlayProps> = ({
  overlayTarget,
  overlayColor,
  spotlightPadding,
  spotlightRadius,
  onOverlayClick,
  onLoad,
  shouldShowPopover,
  ...tourPopoverProps
}) => {
  const { isSmallDevice } = useDeviceChecker();

  const [isAnimating, setIsAnimating] = useState(false);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasCalledOnLoad = useRef(false);

  const finalOverlayTarget =
    overlayTarget || tourPopoverProps.triggerRef?.current;

  // Call onLoad when popover becomes visible
  useEffect(() => {
    if (shouldShowPopover && onLoad && !hasCalledOnLoad.current) {
      hasCalledOnLoad.current = true;
      onLoad();
    } else if (!shouldShowPopover) {
      // Reset the flag when popover is hidden so onLoad can be called again next time
      hasCalledOnLoad.current = false;
    }
  }, [shouldShowPopover, onLoad]);

  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  const handleOverlayClick = useCallback(() => {
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }

    setIsAnimating(true);

    animationTimeoutRef.current = setTimeout(() => {
      setIsAnimating(false);
      animationTimeoutRef.current = null;
    }, TOUR_ANIMATION_DURATION);

    onOverlayClick?.();
  }, [onOverlayClick]);

  if (isSmallDevice) {
    return null;
  }

  return (
    <TourOverlay
      shouldShowOverlay={shouldShowPopover}
      target={finalOverlayTarget}
      {...(overlayColor && { overlayColor })}
      {...(spotlightPadding && { spotlightPadding })}
      {...(spotlightRadius && { spotlightRadius })}
      onOverlayClick={handleOverlayClick}
    >
      <TourPopover
        shouldShowPopover={shouldShowPopover}
        animate={isAnimating}
        {...tourPopoverProps}
      />
    </TourOverlay>
  );
};
